import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OnboardingStatus, User } from '@demind-inc/core';

import { accountApi } from '../api';

interface UseUpdateOnboardingStatusParams {
  userId: string;
  newStatus: Partial<OnboardingStatus>;
}

export const useUpdateOnboardingStatus = () => {
  const queryClient = useQueryClient();

  const updateOnboardingStatusMutation = useMutation({
    mutationFn: ({ userId, newStatus }: UseUpdateOnboardingStatusParams) => {
      return accountApi.updateOnboardingStatus(userId, newStatus).then((data) => data);
    },
    onMutate: async ({ userId, newStatus }) => {
      await queryClient.cancelQueries({ queryKey: ['lifestack.user'] });

      const userQueryKey = ['lifestack.user', { userId }] as const;
      const previousUser = queryClient.getQueryData<User>(userQueryKey);

      queryClient.setQueryData<User>(userQueryKey, (currentUser) => {
        if (!currentUser) {
          return currentUser;
        }
        return {
          ...currentUser,
          onboarding: {
            ...(currentUser.onboarding || {}),
            ...newStatus,
          },
        };
      });

      return { previousUser, userQueryKey };
    },
    onError: (_error, _variables, context) => {
      if (!context?.previousUser) {
        return;
      }
      queryClient.setQueryData(context.userQueryKey, context.previousUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.user'] });
    },
  });

  return {
    updateOnboardingStatus: updateOnboardingStatusMutation.mutateAsync,
    ...updateOnboardingStatusMutation,
  };
};
